import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { IMosaicTabNode, MosaicDirection } from "src/utils/MosaicTree";
import { insertTabNode } from "src/redux/actions/Mosaic.Actions";
import { COMPONENT_MAP, ViewId } from "src/redux/reducers/MosaicManagerReducer";
import { getCustomerComponent } from "src/features/featureRegistry";
import { Grid } from "@mui/material";
import { useDrop, DropTargetMonitor } from "react-dnd";
import { MosaicPreviewPanel } from "./MosaicPreviewPanel";
import { WindowErrorBoundary } from "./WindowErrorBoundary";

interface IMosaicTabPanelProps {
  tabItem?: IMosaicTabNode;
  isHidden?: boolean;
  numTabsOpen?: number;
  splitPercentage?: number;
  clientOffset?: any;
  isOver?: boolean;
  path?: string[];
}

interface IPreviewPanelDimensions {
  x: number;
  y: number;
  width: number;
  height: number;
  direction?: MosaicDirection;
}

interface DragItem {
  mosaicTabNode: IMosaicTabNode;
  numTabsOpen: number;
  path: string[];
  // type: string;
}

const getDimensions = (monitor: any, tabPanel: HTMLElement): IPreviewPanelDimensions => {
  const quarterWidth = tabPanel.clientWidth / 4.0;
  const quarterHeight = tabPanel.clientHeight / 4.0;

  const newPreviewPanelDimensions: IPreviewPanelDimensions = {
    x: 0,
    y: 0,
    width: quarterWidth * 2.0,
    height: tabPanel.clientHeight,
    direction: null,
  };

  const componentPosition: DOMRect = tabPanel.getBoundingClientRect();
  const viewPortPosition = monitor.getClientOffset();

  const previewX = viewPortPosition.x - componentPosition.left;
  const previewY = viewPortPosition.y - componentPosition.top;

  if (previewX >= quarterWidth * 3.0) {
    newPreviewPanelDimensions.x = quarterWidth * 2.0;
    newPreviewPanelDimensions.direction = MosaicDirection.Right;
  } else if (previewY <= quarterHeight) {
    newPreviewPanelDimensions.height = quarterHeight * 2.0;
    newPreviewPanelDimensions.width = tabPanel.clientWidth;
    newPreviewPanelDimensions.direction = MosaicDirection.Top;
  } else if (previewY >= quarterHeight * 3.0) {
    newPreviewPanelDimensions.y = quarterHeight * 2.0;
    newPreviewPanelDimensions.height = quarterHeight * 2.0;
    newPreviewPanelDimensions.width = tabPanel.clientWidth;
    newPreviewPanelDimensions.direction = MosaicDirection.Bottom;
  } else if (previewX <= quarterWidth) {
    newPreviewPanelDimensions.direction = MosaicDirection.Left;
  } else {
    newPreviewPanelDimensions.direction = MosaicDirection.Center;
    newPreviewPanelDimensions.height = tabPanel.clientHeight;
    newPreviewPanelDimensions.width = tabPanel.clientWidth;
  }

  // Add offset if tab is not first
  newPreviewPanelDimensions.x += tabPanel.offsetLeft;
  newPreviewPanelDimensions.y += tabPanel.offsetTop;
  return newPreviewPanelDimensions;
};

export const MosaicTabPanel = (props: IMosaicTabPanelProps): JSX.Element => {
  const { tabItem, isHidden, numTabsOpen, splitPercentage, path } = props;
  const dispatch = useDispatch();

  const PanelComponent = useMemo(
    () => getCustomerComponent(tabItem.componentName) ?? COMPONENT_MAP[tabItem.componentName as ViewId],
    [tabItem.componentName]
  );
  const [previewPanelDimensions, setPreviewPanelDimensions] = useState<IPreviewPanelDimensions>(null);
  const [hasUpdated, setHasUpdated] = useState<boolean>(false);

  const updatePreviewPosition = useCallback(
    (monitor: DropTargetMonitor): void => {
      const tabPanel: HTMLElement = document.getElementById(`${tabItem.id}-panel`);
      if (hasUpdated || !tabPanel) {
        return;
      }
      setHasUpdated(true);
      const newPreviewPanelDimensions = getDimensions(monitor, tabPanel);
      setPreviewPanelDimensions(newPreviewPanelDimensions);
      setTimeout(() => {
        setHasUpdated(false);
      }, 100);
    },
    [hasUpdated, tabItem.id]
  );

  const [{ canDrop, isOver, clientOffset }, drop] = useDrop({
    accept: "DND_NODE",

    drop(i: any, monitor: DropTargetMonitor) {
      const item: DragItem = i;
      const tabPanel: HTMLElement = document.getElementById(`${tabItem.id}-panel`);
      if (!tabPanel) return;
      const newPreviewPanelDimensions = getDimensions(monitor, tabPanel);
      const direction = newPreviewPanelDimensions.direction;
      dispatch(insertTabNode(direction, item.path, path, item.mosaicTabNode));
    },

    canDrop(i: any, monitor: DropTargetMonitor): boolean {
      const item: DragItem = i;
      const itemPath = item.path.join(",");
      const propsPath = path.join(",");
      const canDrop = numTabsOpen > 1 || itemPath !== propsPath;
      return canDrop;
    },

    hover(item: any, monitor: DropTargetMonitor) {
      updatePreviewPosition(monitor);
    },

    collect: (monitor: DropTargetMonitor) => {
      return {
        canDrop: monitor.canDrop(),
        isOver: monitor.isOver(),
        clientOffset: monitor.getClientOffset(),
      };
    },
  });

  const ShowPreview = useMemo(() => {
    return previewPanelDimensions ? <MosaicPreviewPanel {...previewPanelDimensions} /> : <></>;
  }, [previewPanelDimensions]);

  return (
    <div
      key={tabItem.id}
      hidden={isHidden}
      id={`${tabItem.id}-panel`}
      style={{ width: "100%", display: "flex", flexDirection: "column", flexGrow: 1, overflowY: "auto" }}
      ref={drop}
    >
      {isOver && canDrop && previewPanelDimensions ? ShowPreview : null}
      <Grid
        container
        style={{
          width: "100%",
          flexGrow: 1,
          minHeight: 0,
          minWidth: 0,
          flexBasis: 0,
        }}
      >
        <WindowErrorBoundary>
          <PanelComponent
            key={tabItem.id}
            id={tabItem.id}
            path={path}
            splitPercentage={splitPercentage}
            windowProps={tabItem?.props ?? {}} // If props is passed as undefined, set it as an empty object.
            setCloseWarning={tabItem?.setCloseWarning}
          />
        </WindowErrorBoundary>
      </Grid>
    </div>
  );
};
